import React, { useState, useCallback } from 'react';
import {
  Box,
  Text,
  useColorModeValue,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
} from '@chakra-ui/react';
import {
  Table,
  TableHead,
  TableHeaderSortCell,
  TableSortDirection,
  TableBody,
  TableRow,
  TableCell,
  useNexus,
  BarChart,
} from '@spotoninc/nexus-react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  DownloadIcon,
  SalesIcon,
  SendAndPayIcon,
} from '@spotoninc/nexus-web-assets';
import { toPng } from 'html-to-image';
import '../../styles/nexus-table.css';
import '../../styles/consolidated-bar-chart.css';
import '../../styles/custom-table-styles.css';

const parseValue = value => {
  if (value === null) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    // Remove % and any other non-numeric characters except decimal point and minus sign
    return parseFloat(value.replace(/[^0-9.-]+/g, '')) || 0;
  }
  return 0;
};

const formatPercentage = value => {
  if (value === null) return '-';
  const numValue = parseValue(value);
  return `${numValue.toFixed(2)}%`;
};

const formatValue = (value, type) => {
  if (value === null) return '-';
  if (type === 'cost_amt') {
    return `$${parseValue(value).toLocaleString()}`;
  }
  return formatPercentage(value);
};

const transformData = data => {
  // Ensure data is an array before processing
  if (!Array.isArray(data)) {
    console.error('transformData received non-array data:', data);
    return [];
  }

  // Get all unique location names from the data
  const locationNames = [
    ...new Set(
      data.flatMap(item =>
        Object.keys(item).filter(
          key => key !== 'desc' && key !== 'classification'
        )
      )
    ),
  ];

  // Transform the data to match the expected structure
  return data.map(item => ({
    name: item.desc,
    classification: item.classification,
    values: locationNames.reduce((acc, location) => {
      acc[location] = item[location] || null;
      return acc;
    }, {}),
  }));
};

const RecursiveTableRow = ({
  item,
  level = 0,
  expandedSections,
  toggleSection,
  columns,
  type,
  onRowClick,
}) => {
  const hasItems = item.items && item.items.length > 0;
  const itemName = item.name || item.title;
  const isExpanded = expandedSections[itemName];
  const paddingLeft = level * 3;
  const isClickable = hasItems || level > 0;

  const handleClick = () => {
    if (hasItems) {
      toggleSection(itemName);
    }
    if (onRowClick) {
      onRowClick(item);
    }
  };

  return (
    <>
      <TableRow
        variant="bottom-divider"
        onClick={handleClick}
        className="clickable"
        _hover={isClickable ? { bg: 'gray.50' } : {}}
      >
        <TableCell>
          <Box display="flex" alignItems="center" pl={`${paddingLeft}rem`}>
            {hasItems && (
              <Box minWidth="18px" width="18px" mr={2}>
                {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
              </Box>
            )}
            <Box minWidth="18px" width="18px" height="18px" mr={2}>
              {item.classification === 'Revenue' && (
                <SalesIcon width="18px" height="18px" color="#3AB6A5" />
              )}
              {item.classification === 'Expense' && (
                <SendAndPayIcon width="18px" height="18px" color="#ED5E5A" />
              )}
            </Box>
            <Text>
              {itemName.endsWith('($)') ? itemName.slice(0, -3) : itemName}
            </Text>
          </Box>
        </TableCell>
        {columns.map(column => (
          <TableCell key={column} textAlign="left">
            {formatValue(item.values[column], type)}
          </TableCell>
        ))}
      </TableRow>
      {hasItems &&
        isExpanded &&
        item.items.map((subItem, index) => (
          <RecursiveTableRow
            key={index}
            item={subItem}
            level={level + 1}
            expandedSections={expandedSections}
            toggleSection={toggleSection}
            columns={columns}
            type={type}
            onRowClick={onRowClick}
          />
        ))}
    </>
  );
};

const RowDataModal = ({ isOpen, onClose, rowData, columns, type }) => {
  const {
    theme: { tokens },
  } = useNexus();

  const handleDownload = useCallback(
    format => {
      const chartElement = document.querySelector('.nexus-bar-chart');

      if (!chartElement) {
        console.error('Chart element not found');
        return;
      }

      if (format === 'SVG') {
        const svgElement = chartElement.querySelector('svg');
        const svgData = new XMLSerializer().serializeToString(svgElement);
        const svgBlob = new Blob([svgData], {
          type: 'image/svg+xml;charset=utf-8',
        });
        const svgUrl = URL.createObjectURL(svgBlob);
        const downloadLink = document.createElement('a');
        downloadLink.href = svgUrl;
        downloadLink.download = `${rowData.name}_chart.svg`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(svgUrl);
      } else if (format === 'PNG') {
        toPng(chartElement)
          .then(dataUrl => {
            const link = document.createElement('a');
            link.download = `${rowData.name}_chart.png`;
            link.href = dataUrl;
            link.click();
          })
          .catch(err => {
            console.error('Error generating PNG:', err);
          });
      } else if (format === 'CSV') {
        const headers = ['Location', 'Value'];

        // Helper function to escape CSV values
        const escapeCSV = value => {
          // If the value contains commas, quotes, or newlines, wrap it in quotes
          // Also escape any existing quotes by doubling them
          if (
            value &&
            (value.includes(',') || value.includes('"') || value.includes('\n'))
          ) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        };

        const rows = columns.map(column => [
          escapeCSV(column),
          escapeCSV(formatValue(rowData.values[column], type)),
        ]);

        const csvContent = [
          headers.map(escapeCSV).join(','),
          ...rows.map(row => row.join(',')),
        ].join('\n');

        const blob = new Blob([csvContent], {
          type: 'text/csv;charset=utf-8;',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `${rowData.name}_data.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    },
    [rowData, columns, type]
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
      <ModalOverlay />
      <ModalContent maxW="95vw" maxH="90vh" m={2}>
        <ModalHeader p={4}>
          <HStack justify="space-between" width="100%">
            <Text
              style={{
                color: tokens.semanticColorTextNeutralStrongestNone,
                fontFamily: tokens.primitiveFontFamilySans,
                fontSize: tokens.primitiveFontSize300,
                fontWeight: tokens.primitiveFontWeightSemibold,
              }}
            >
              <HStack spacing={4}>
                {rowData.classification === 'Revenue' && (
                  <SalesIcon width="22px" height="22px" color="#3AB6A5" />
                )}
                {rowData.classification === 'Expense' && (
                  <SendAndPayIcon width="22px" height="22px" color="#ED5E5A" />
                )}
                <Text>
                  {rowData.name.endsWith('($)')
                    ? rowData.name.slice(0, -3)
                    : rowData.name}
                </Text>
              </HStack>
            </Text>
            <HStack spacing={2}>
              <Menu>
                <MenuButton
                  as={IconButton}
                  icon={<DownloadIcon width="24px" height="24px" />}
                  variant="tonal"
                  aria-label="Download options"
                  _hover={{ bg: 'gray.100' }}
                  _active={{ bg: 'gray.200' }}
                  size="md"
                  borderRadius="100%"
                />
                <MenuList
                  minW="200px"
                  py={2}
                  border="1px solid"
                  borderColor="gray.200"
                  boxShadow="md"
                  bg="#F4F7FA"
                >
                  <MenuItem
                    py={2}
                    px={4}
                    fontSize="sm"
                    fontWeight="400"
                    _hover={{ bg: 'gray.50' }}
                    _focus={{ bg: 'gray.50' }}
                    onClick={() => handleDownload('SVG')}
                    bg="#F4F7FA"
                  >
                    Download SVG
                  </MenuItem>
                  <MenuItem
                    py={2}
                    px={4}
                    fontSize="sm"
                    fontWeight="400"
                    _hover={{ bg: 'gray.50' }}
                    _focus={{ bg: 'gray.50' }}
                    onClick={() => handleDownload('PNG')}
                    bg="#F4F7FA"
                  >
                    Download PNG
                  </MenuItem>
                  <MenuItem
                    py={2}
                    px={4}
                    fontSize="sm"
                    fontWeight="400"
                    _hover={{ bg: 'gray.50' }}
                    _focus={{ bg: 'gray.50' }}
                    onClick={() => handleDownload('CSV')}
                    bg="#F4F7FA"
                  >
                    Download CSV
                  </MenuItem>
                </MenuList>
              </Menu>
              <ModalCloseButton position="relative" top={0} right={0} />
            </HStack>
          </HStack>
        </ModalHeader>
        <ModalBody p={2}>
          <Box height="500px" width="100%">
            <BarChart
              series={[
                {
                  type: 'column',
                  data: columns.map(column => {
                    const value = rowData.values[column];
                    return value ? parseValue(value) : 0;
                  }),
                  name: rowData.name,
                  stack: 0,
                  color:
                    rowData.classification === 'Revenue'
                      ? '#3AB6A5'
                      : rowData.classification === 'Expense'
                        ? '#ED5E5A'
                        : undefined,
                },
              ]}
              categories={columns}
              showLegend={false}
              yAxisLabelFormat={type === 'cost_amt' ? '${value}' : '%{value}'}
              tooltip={{
                valueDecimals: 2,
                valuePrefix: type === 'cost_amt' ? '$' : '',
                valueSuffix: type === 'cost_amt' ? '' : '%',
                backgroundColor: '#FFFFFF',
                borderWidth: 0,
                borderRadius: 12,
                shadow: true,
                useHTML: true,
                style: {
                  zIndex: 99999,
                },
                formatter: function () {
                  const value = this.y;
                  const formattedValue =
                    type === 'cost_amt'
                      ? `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : `${value.toFixed(2)}%`;
                  return `<div class="tooltip-container" style="z-index: 9999; width: auto; min-width: 200px; max-width: 300px;">
                    <div class="tooltip-header" style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${this.x}</div>
                    <div class="tooltip-row">
                      <div class="tooltip-label-container" style="max-width: 70%; overflow: hidden;">
                        <div class="tooltip-color-indicator" style="background-color: ${this.color || '#3AB6A5'};"></div>
                        <span class="tooltip-label" style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${rowData.name}</span>
                      </div>
                      <span class="tooltip-value">${formattedValue}</span>
                    </div>
                  </div>`;
                },
              }}
              xAxis={{
                labels: {
                  style: {
                    color: tokens.semanticColorTextNeutralLightestNone,
                    fontSize: tokens.primitiveFontSize75,
                    fontWeight: tokens.primitiveFontWeightRegular,
                    lineHeight: tokens.primitiveFontLineHeight200,
                    letterSpacing: tokens.primitiveFontLetterSpacingWide,
                    fontFamily: tokens.primitiveFontFamilySans,
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    width: `${(window.innerWidth * 0.8) / columns.length}px`,
                    height: 'auto',
                    textAlign: 'center',
                    overflow: 'hidden',
                  },
                  rotation: 0,
                  step: 1,
                  y: 40,
                  align: 'center',
                  reserveSpace: true,
                  staggerLines: 2,
                },
                tickLength: 0,
                lineWidth: 0,
                maxPadding: 0.05,
                minPadding: 0.05,
              }}
              yAxis={{
                allowDecimals: false,
                labels: {
                  formatter: function () {
                    if (type === 'cost_amt') {
                      const value = this.value;
                      if (value >= 1000000) {
                        return `$${(value / 1000000).toFixed(0)}M`;
                      } else if (value >= 1000) {
                        return `$${(value / 1000).toFixed(0)}K`;
                      }
                      return `$${value.toLocaleString()}`;
                    }
                    return `${this.value}%`;
                  },
                  align: 'right',
                  style: {
                    color: tokens.semanticColorTextNeutralLightestNone,
                    fontSize: tokens.primitiveFontSize75,
                    fontWeight: tokens.primitiveFontWeightRegular,
                    lineHeight: tokens.primitiveFontLineHeight200,
                    letterSpacing: tokens.primitiveFontLetterSpacingWide,
                    fontFamily: tokens.primitiveFontFamilySans,
                  },
                },
                startOnTick: true,
                endOnTick: true,
                gridLineDashStyle: 'Dot',
                gridLineWidth: 1,
                tickAmount: 6,
                plotLines: [
                  {
                    value: 0,
                    width: 1,
                    color: '#808080',
                  },
                ],
                min: null,
                max: null,
              }}
              plotOptions={{
                column: {
                  grouping: true,
                  groupPadding: 0.15,
                  pointPadding: 0.1,
                  borderWidth: 0,
                  shadow: false,
                  minPointLength: 3,
                },
              }}
              chart={{
                spacing: [20, 10, 20, 20],
                style: {
                  fontFamily: 'Poppins, sans-serif',
                },
              }}
              scrollableAreaMinWidth={500}
              className="nexus-bar-chart"
            />
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const LocationAnalysisTable = ({ data, columns, loading, type }) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const [expandedSections, setExpandedSections] = useState({});
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: TableSortDirection.ascending,
  });
  const [selectedRow, setSelectedRow] = useState(null);
  const {
    theme: { tokens },
  } = useNexus();

  const toggleSection = sectionName => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName],
    }));
  };

  const handleSort = (key, newDirection) => {
    setSortConfig({ key, direction: newDirection });
  };

  const sortData = data => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a.values[sortConfig.key];
      let bValue = b.values[sortConfig.key];

      // Handle null values
      if (aValue === null) aValue = 0;
      if (bValue === null) bValue = 0;

      // Parse values if they're strings
      if (typeof aValue === 'string') {
        aValue = parseValue(aValue);
      }
      if (typeof bValue === 'string') {
        bValue = parseValue(bValue);
      }

      if (sortConfig.key === 'desc') {
        // Sort by description
        if (a.name < b.name)
          return sortConfig.direction === TableSortDirection.ascending ? -1 : 1;
        if (a.name > b.name)
          return sortConfig.direction === TableSortDirection.ascending ? 1 : -1;
        return 0;
      } else {
        // Sort by numeric values
        if (sortConfig.direction === TableSortDirection.ascending) {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      }
    });
  };

  const transformedData = sortData(transformData(data));

  const handleRowClick = item => {
    setSelectedRow(item);
  };

  return (
    <>
      <Box bg={bgColor} borderColor={borderColor} overflow="hidden">
        <Box overflowX="auto" maxHeight="600px">
          <Table
            fontSize={tokens.fontSize100}
            fontWeight={600}
            stickyFirstColumn
            className="nexus-table-custom-divider"
          >
            <TableHead sticky>
              <TableRow variant="filled">
                <TableHeaderSortCell
                  onSort={direction => handleSort('desc', direction)}
                  direction={
                    sortConfig.key === 'desc' ? sortConfig.direction : undefined
                  }
                >
                  Description
                </TableHeaderSortCell>
                {columns.map(column => (
                  <TableHeaderSortCell
                    key={column}
                    onSort={direction => handleSort(column, direction)}
                    direction={
                      sortConfig.key === column
                        ? sortConfig.direction
                        : undefined
                    }
                    textAlign="right"
                  >
                    {column}
                  </TableHeaderSortCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} textAlign="center">
                    <Text>Loading...</Text>
                  </TableCell>
                </TableRow>
              ) : transformedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} textAlign="center">
                    <Text>No data available</Text>
                  </TableCell>
                </TableRow>
              ) : (
                transformedData.map((item, index) => (
                  <RecursiveTableRow
                    key={index}
                    item={item}
                    level={0}
                    expandedSections={expandedSections}
                    toggleSection={toggleSection}
                    columns={columns}
                    type={type}
                    onRowClick={handleRowClick}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </Box>
      {selectedRow && (
        <RowDataModal
          isOpen={!!selectedRow}
          onClose={() => setSelectedRow(null)}
          rowData={selectedRow}
          columns={columns}
          type={type}
        />
      )}
    </>
  );
};

export default LocationAnalysisTable;
